import { useQuery } from "@tanstack/react-query";
import { CourseJobsResponse } from "@vixi/types";
import api from "@vixi/api";
import { logger } from "@vixi/utils";

/**
 * Hook to fetch all course generation jobs
 * Used on Dashboard to show in-progress courses
 */
export const useCourseJobs = () => {
  const query = useQuery({
    queryKey: ["course-jobs"],
    queryFn: async () => {
      // ============================================
      // GET /api/CourseMaker/jobs?pageNumber=1&pageSize=100
      // ============================================
      // This fetches all jobs (in progress and completed)
      // Required params: pageNumber, pageSize (lowercase - same as /api/CourseMaker/all)
      // Optional params: Status
      // Response: { items: CourseJobStatus[], pageNumber, totalPages, ... }
      // Used on Dashboard to show "In Progress Courses" section

      const endpoint = api.endpoints.getCourseJobs;
      const baseURL = api.getDefaultConfig().baseURL || "/api";
      
      // Required parameters - using lowercase as API expects (same as /api/CourseMaker/all)
      // API expects: pageNumber (integer, 1-indexed), pageSize (integer)
      const pageNumber = 1; // Start from first page (1-indexed, same as /api/CourseMaker/all)
      const pageSize = 100; // Get up to 100 jobs (should be enough for dashboard)
      
      // IMPORTANT: Use lowercase parameter names (pageNumber, pageSize) - same as /api/CourseMaker/all
      const queryParams = {
        pageNumber: pageNumber,
        pageSize: pageSize,
      };

      const fullURL = `${baseURL}/${endpoint}?pageNumber=${pageNumber}&pageSize=${pageSize}`;
      logger.info(`📥 Fetching all course jobs from GET ${fullURL}`);
      logger.info(`📥 Query params object:`, queryParams);
      logger.info(`📥 pageNumber type:`, typeof pageNumber, `value:`, pageNumber);
      logger.info(`📥 pageSize type:`, typeof pageSize, `value:`, pageSize);

      try {
        const response = await api.get<CourseJobsResponse>(endpoint, {
          params: queryParams,
        });
        
        // Log the actual request URL that was sent
        logger.info(`✅ Request completed successfully`);
        logger.info(`✅ Response items count:`, response.data.items.length);

        logger.info(`✅ Got ${response.data.items.length} jobs from API`);

        return response.data;
      } catch (error: any) {
        logger.error(`❌ Failed to fetch course jobs:`, error);
        logger.error(`❌ Error response:`, error?.response?.data);
        logger.error(`❌ Error status:`, error?.response?.status);
        
        // Re-throw to let React Query handle it
        throw error;
      }
    },
    // Poll every 3 seconds to update progress percentage for in-progress jobs
    // This ensures the floating card shows real-time progress even if user navigates away
    refetchInterval: 3000, // 3 seconds - same as useCourseJobStatus
    // Don't retry on 500 errors (server errors)
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 500) {
        return false; // Don't retry on 500 errors
      }
      return failureCount < 3;
    },
  });

  return query;
};
